import { Button, Modal } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { usePermission } from "@how/app-kit";
import { DataTable, FilterBar, PageHeader, useSearchParamState } from "@how/ui";
import { CrmTabs } from "./CrmTabs";
import { HouseholdDrawer } from "./HouseholdDrawer";
import { HouseholdForm } from "./HouseholdForm";
import {
  useCreateHousehold,
  useHouseholds,
  type HouseholdRow,
  type HouseholdSort,
} from "./household-queries";

const PAGE_SIZE = 25;
const SORTABLE = new Set<HouseholdSort["id"]>(["name", "created_at"]);
const EMPTY = {
  name: "",
  addressLine1: "",
  addressLine2: "",
  locality: "",
  region: "",
  postalCode: "",
};

const columns: ColumnDef<HouseholdRow>[] = [
  { accessorKey: "name", header: "Household" },
  {
    accessorKey: "memberCount",
    header: "Members",
    enableSorting: false,
    meta: { numeric: true },
  },
  {
    accessorKey: "locality",
    header: "City",
    enableSorting: false,
    cell: (ctx) => {
      const row = ctx.row.original;
      return [row.locality, row.region].filter((part) => part !== null).join(", ");
    },
  },
  {
    accessorKey: "created_at",
    header: "Added",
    meta: { numeric: true },
    cell: (ctx) => new Date(ctx.row.original.created_at).toLocaleDateString(),
  },
];

/** The households list, on the reviewed people-directory pattern: server-side everything, state in the URL. */
export function HouseholdsPage() {
  const [search, setSearch] = useSearchParamState("q");
  const [page, setPage] = useSearchParamState("page", "1");
  const [sortParam, setSortParam] = useSearchParamState("sort", "name.asc");
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const [sortId, sortDir] = sortParam.split(".");
  const sort: HouseholdSort = {
    id: SORTABLE.has(sortId as HouseholdSort["id"]) ? (sortId as HouseholdSort["id"]) : "name",
    desc: sortDir === "desc",
  };
  const pageIndex = Math.max(0, Number.parseInt(page, 10) - 1 || 0);

  const households = useHouseholds({ search, pageIndex, pageSize: PAGE_SIZE, sort });
  const canManage = usePermission("households.manage", { write: true });
  const createHousehold = useCreateHousehold();

  const sorting = useMemo<SortingState>(
    () => [{ id: sort.id, desc: sort.desc }],
    [sort.id, sort.desc],
  );
  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex, pageSize: PAGE_SIZE }),
    [pageIndex],
  );
  const status = households.isPending ? "loading" : households.isError ? "error" : "ready";

  return (
    <>
      <PageHeader
        title="People"
        description="Everyone your church knows, whether or not they have a login."
        actions={
          canManage.data === true ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setAdding(true);
              }}
            >
              Add household
            </Button>
          ) : undefined
        }
      />
      <CrmTabs active="households" />
      <FilterBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(undefined);
        }}
        searchPlaceholder="Search households"
      />
      <DataTable
        label="Households"
        columns={columns}
        rows={households.data?.rows ?? []}
        totalRows={households.data?.total ?? 0}
        status={status}
        pagination={pagination}
        onPaginationChange={(updater) => {
          const next = typeof updater === "function" ? updater(pagination) : updater;
          setPage(String(next.pageIndex + 1));
        }}
        sorting={sorting}
        onSortingChange={(updater) => {
          const next = typeof updater === "function" ? updater(sorting) : updater;
          const first = next[0];
          setSortParam(
            first === undefined ? undefined : `${first.id}.${first.desc ? "desc" : "asc"}`,
          );
        }}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          setSelected(row.id);
        }}
        emptyTitle={search.trim().length > 0 ? "No household matches" : "No households yet"}
        emptyDescription={
          search.trim().length > 0
            ? "Try a different name."
            : "A household groups the people who share an address."
        }
        errorMessage="Could not load households."
        onRetry={() => {
          void households.refetch();
        }}
      />

      <HouseholdDrawer
        householdId={selected}
        onClose={() => {
          setSelected(null);
        }}
      />

      <Modal
        opened={adding}
        onClose={() => {
          setAdding(false);
        }}
        title="Add a household"
        radius="lg"
      >
        <HouseholdForm
          initial={EMPTY}
          submitLabel="Add household"
          loading={createHousehold.isPending}
          failed={createHousehold.isError}
          onCancel={() => {
            setAdding(false);
          }}
          onSubmit={(fields) => {
            createHousehold.mutate(fields, {
              onSuccess: (householdId) => {
                setAdding(false);
                // Open it straight away: the next step is always adding its members.
                setSelected(householdId);
              },
            });
          }}
        />
      </Modal>
    </>
  );
}
